import { useDispatch, useSelector } from 'react-redux'
import { useSession } from 'next-auth/react'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { logUserOut } from '../../state/actions/user/user'
import { unauthorizedError } from '../show-error-dialog/showErrorDialog'
import { clickCreateJob } from '../../state/actions/ui/ui'
import { State } from '../../state/reducers'
import { getBasket } from '../../state/actions/webshop/webshop'
import { useAppSelector } from '../../state/reducers/types'

export type PageLayoutInputs = {
  menuIsOpen: boolean
  handleMenuToggle: (newValue?: boolean) => void
  loggedIn: boolean
  onClickPlaceVacancy: () => void
  clickPlaceVacancyIsInProgress: boolean
  fullName: string
  onLogInOut: () => void
  itemsInCart: number
}

export const usePageLayout = (): PageLayoutInputs => {
  const session = useSession()
  const dispatch = useDispatch()
  const router = useRouter()
  const [loggedIn, setLoggedIn] = useState(session.status === 'authenticated')
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const inProgressElementId = useSelector<State, string>(state => state.ui.inProgressElementId)
  const sideBarCreateJobElementId = 'side-bar-create-job-element-id'
  const onLogInOut = () => {
    dispatch(logUserOut())
  }
  const itemsInCart = useAppSelector(state =>
    state.webshop.inOrder.reduce((previous, current) => previous + current.amount, 0),
  )


  useEffect(() => {
    const closeMenuOnRouteChange = () => {
      if (menuIsOpen) {
        setMenuIsOpen(false)
      }
    }

    router?.events?.on('routeChangeComplete', closeMenuOnRouteChange)

    return () => {
      router?.events?.off('routeChangeComplete', closeMenuOnRouteChange)
    }
  }, [menuIsOpen])

  useEffect(() => {
    // basket should be fetched for logged in users
    // when users return from the external payment provider after a succesful payment, their basket is empty
    // we still want to show the contents of the (previous) basket, and use the product codes in the query parameters to rebuild it
    if (loggedIn && !router?.query?.productCodes) {
      dispatch(getBasket())
    }
  }, [loggedIn])

  const handleMenuToggle = (state?: boolean) => {
    if (typeof state === 'boolean') {
      setMenuIsOpen(state)
    } else {
      setMenuIsOpen(!menuIsOpen)
    }
  }

  useEffect(() => {
    setLoggedIn(session.status === 'authenticated')
  }, [session.status])

  const onClickPlaceVacancy = () => {
    if (session.status === 'unauthenticated') {
      return dispatch(unauthorizedError(dispatch))
    }
    return dispatch(clickCreateJob(sideBarCreateJobElementId))
  }

  return {
    menuIsOpen,
    handleMenuToggle,
    loggedIn,
    onClickPlaceVacancy,
    clickPlaceVacancyIsInProgress: inProgressElementId === sideBarCreateJobElementId,
    fullName: loggedIn ? session.data?.user?.name : '',
    onLogInOut,
    itemsInCart,
  }
}
